import { useCallback, useRef } from 'react'
import { defaultImageProcessingConfig } from '@/config/imageProcessing'
import { compressWithFallback, probeDecodable, validateImageFile, validateOriginalImageSize, validateImageDimensions } from '@/utils/imageProcessing'
import { ProcessingError, type ImageProcessingConfig, type ProcessedImageResult } from '@/types/imageProcessing'

/**
 * Module-level semaphore so concurrent compressions are capped across the
 * whole app (every PhotoStrip instance shares the same worker pool budget),
 * not just within one hook instance.
 */
let activeCompressions = 0
const waitQueue: Array<() => void> = []

async function acquireSlot(maxConcurrent: number): Promise<void> {
  if (activeCompressions < maxConcurrent) {
    activeCompressions += 1
    return
  }
  await new Promise<void>((resolve) => waitQueue.push(resolve))
  activeCompressions += 1
}

function releaseSlot(): void {
  activeCompressions = Math.max(0, activeCompressions - 1)
  const next = waitQueue.shift()
  if (next) next()
}

export function useImageProcessor(config: ImageProcessingConfig = defaultImageProcessingConfig) {
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  const processImage = useCallback(
    async (file: File, key: string): Promise<ProcessedImageResult> => {
      const controller = new AbortController()
      abortControllersRef.current.set(key, controller)

      try {
        validateImageFile(file)
        validateOriginalImageSize(file, config)
        const { width, height } = await probeDecodable(file)
        validateImageDimensions(width, height, config)
        if (controller.signal.aborted) {
          throw new ProcessingError('aborted', 'Processing cancelled')
        }

        const maxBytes = config.maxSizeMB * 1024 * 1024
        if (file.size <= maxBytes) {
          return {
            file,
            wasCompressed: false,
            originalSizeBytes: file.size,
            finalSizeBytes: file.size,
            width,
            height,
          }
        }

        await acquireSlot(config.maxConcurrentCompressions)
        try {
          return await compressWithFallback(file, config, controller.signal)
        } finally {
          releaseSlot()
        }
      } finally {
        abortControllersRef.current.delete(key)
      }
    },
    [config]
  )

  const cancel = useCallback((key: string) => {
    abortControllersRef.current.get(key)?.abort()
    abortControllersRef.current.delete(key)
  }, [])

  return { processImage, cancel }
}

export { ProcessingError }
export type { ProcessedImageResult }
